import { useState, useCallback, useEffect, useRef } from "react";
import FloorPlanCanvas from "./FloorPlanCanvas";
import RoomEditor from "./RoomEditor";
import DoorEditor from "./DoorEditor";
import StairEditor from "./StairEditor";
import NodeEditor from "./NodeEditor";
import LightEditor from "./LightEditor";
import DwellEditor from "./DwellEditor";
import PathEditor from "./PathEditor";
import { INITIAL_FLOOR_PLAN } from "./initialFloorPlan";
import { normalizeFloorPlan, updateMapNode } from "./nodeSettings";
import { roomAt, suggestPlacement, samplePath, coveredByAny, dwellCoverage, dwellCenter, coveredByReliable } from "./geometry";
import { apiClient } from "../../services/api";
import "./HouseMap.css";

const STORAGE_KEY = "anna_house_map";

const NODE_KIND_LABELS = {
  full: "Full (cam+sensor+lux)",
  full_nolux: "Full (cam+sensor)",
  simple: "Simple (sensor+lux)",
  simple_nolux: "Simple (sensor)",
};

function nodeKindFlags(kind) {
  return {
    hasCamera: kind === "full" || kind === "full_nolux",
    hasLux: kind === "full" || kind === "simple",
  };
}

function loadFloorPlan() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return normalizeFloorPlan(JSON.parse(saved));
  } catch (e) {
    console.error("Failed to load floor plan:", e);
  }
  return normalizeFloorPlan(INITIAL_FLOOR_PLAN);
}

function ContainerEditor({ floorPlan, onUpdate }) {
  const [width, setWidth] = useState(String(floorPlan.width));
  const [height, setHeight] = useState(String(floorPlan.height));

  const handleWidthBlur = () => {
    const v = parseFloat(width);
    if (!isNaN(v) && v > 0) onUpdate({ width: v });
    else setWidth(String(floorPlan.width));
  };

  const handleHeightBlur = () => {
    const v = parseFloat(height);
    if (!isNaN(v) && v > 0) onUpdate({ height: v });
    else setHeight(String(floorPlan.height));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const totalArea = floorPlan.rooms.reduce((sum, room) => {
    let area = 0;
    const n = room.polygon.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += room.polygon[i].x * room.polygon[j].y;
      area -= room.polygon[j].x * room.polygon[i].y;
    }
    return sum + Math.abs(area / 2);
  }, 0);

  return (
    <div className="container-editor">
      <h4>Apartment Container</h4>
      <p className="editor-hint">Set the overall bounding box of the apartment. The container does not need to be rectangular — rooms define the actual shape.</p>
      <div className="container-dims">
        <div className="dim-field">
          <label>Width (m)</label>
          <input
            type="number"
            step="0.5"
            min="1"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            onBlur={handleWidthBlur}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="dim-field">
          <label>Depth (m)</label>
          <input
            type="number"
            step="0.5"
            min="1"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onBlur={handleHeightBlur}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="dim-field dim-readonly">
          <label>Total Room Area</label>
          <span className="dim-value">{totalArea.toFixed(1)} m²</span>
        </div>
      </div>
    </div>
  );
}

function HouseMap() {
  const [floorPlan, setFloorPlan] = useState(loadFloorPlan);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedDoorId, setSelectedDoorId] = useState(null);
  const [selectedStairId, setSelectedStairId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedLightId, setSelectedLightId] = useState(null);
  const [selectedDwellId, setSelectedDwellId] = useState(null);
  const [nodeRegistry, setNodeRegistry] = useState([]);
  const [showGrid, setShowGrid] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [layers, setLayers] = useState({
    structure: true,
    nodes: true,
    lights: true,
    dwell: true,
    paths: true,
  });
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [nodeSubOpen, setNodeSubOpen] = useState(false);
  const [drawingPath, setDrawingPath] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);
  const [selectedPathId, setSelectedPathId] = useState(null);
  // "idle" | "loading" | "saving" | "saved" | "error"
  const [syncStatus, setSyncStatus] = useState("loading");

  const saveTimer = useRef(null);
  const didLoad = useRef(false);

  const selectedRoom = floorPlan.rooms.find((r) => r.id === selectedRoomId);
  const selectedDoor = (floorPlan.doors || []).find((d) => d.id === selectedDoorId);
  const selectedStair = (floorPlan.stairs || []).find((s) => s.id === selectedStairId);
  const selectedNode = (floorPlan.nodes || []).find((n) => n.id === selectedNodeId);
  const selectedLight = (floorPlan.lights || []).find((l) => l.id === selectedLightId);
  const selectedDwell = (floorPlan.dwellZones || []).find((z) => z.id === selectedDwellId);
  const selectedPath = (floorPlan.paths || []).find((p) => p.id === selectedPathId);

  const roomNameById = useCallback(
    (id) => floorPlan.rooms.find((r) => r.id === id)?.name || null,
    [floorPlan.rooms]
  );

  const clearSelection = useCallback(() => {
    setSelectedRoomId(null);
    setSelectedDoorId(null);
    setSelectedStairId(null);
    setSelectedNodeId(null);
    setSelectedLightId(null);
    setSelectedDwellId(null);
    setSelectedPathId(null);
  }, []);

  // Overall walk-path coverage fraction (0..1) across all paths.
  const overallCoverage = (() => {
    const paths = floorPlan.paths || [];
    const nodes = floorPlan.nodes || [];
    let total = 0;
    let covered = 0;
    for (const path of paths) {
      const samples = samplePath(path.points || [], 0.2);
      for (const p of samples) {
        total++;
        if (coveredByAny(p, nodes)) covered++;
      }
    }
    return total === 0 ? null : covered / total;
  })();

  // Dwell (resting place) reliable-ring coverage fraction (0..1), or null.
  const dwellCov = dwellCoverage(floorPlan.dwellZones, floorPlan.nodes);

  // MAC addresses already linked to a placed node marker.
  const usedMacs = (floorPlan.nodes || []).map((n) => n.mac).filter(Boolean);
  // Registry nodes detected by the presence engine but not yet placed on the map.
  const unlinkedNodes = nodeRegistry.filter((n) => !usedMacs.includes(n.mac));

  // Fetch the hardware node registry (auto-registered by the presence engine).
  const refreshNodes = useCallback(async () => {
    try {
      const res = await apiClient.get("/nodes");
      if (res && Array.isArray(res.nodes)) setNodeRegistry(res.nodes);
    } catch (e) {
      console.error("Failed to load node registry:", e);
    }
  }, []);

  // Load from server on mount; fall back to localStorage/INITIAL on failure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get("/house-map");
        if (cancelled) return;
        if (res && res.floorPlan && Array.isArray(res.floorPlan.rooms)) {
          const plan = normalizeFloorPlan(res.floorPlan);
          setFloorPlan(plan);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
        }
        setSyncStatus("saved");
      } catch (e) {
        console.error("Failed to load house map from server:", e);
        if (!cancelled) setSyncStatus("error");
      } finally {
        if (!cancelled) didLoad.current = true;
      }
    })();
    refreshNodes();
    return () => {
      cancelled = true;
    };
  }, [refreshNodes]);

  // Persist: localStorage immediately (cache) + debounced PUT to server.
  const saveFloorPlan = useCallback((updatedPlan) => {
    const plan = normalizeFloorPlan(updatedPlan);
    setFloorPlan(plan);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));

    setSyncStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await apiClient.put("/house-map", plan);
        setSyncStatus("saved");
      } catch (e) {
        console.error("Failed to save house map to server:", e);
        setSyncStatus("error");
      }
    }, 1000);
  }, []);

  // Flush any pending save on unmount.
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const handleRoomSelect = useCallback((roomId) => {
    clearSelection();
    setSelectedRoomId(roomId);
  }, [clearSelection]);

  const handleDoorSelect = useCallback((doorId) => {
    clearSelection();
    setSelectedDoorId(doorId);
  }, [clearSelection]);

  const handleStairSelect = useCallback((stairId) => {
    clearSelection();
    setSelectedStairId(stairId);
  }, [clearSelection]);

  const handleNodeSelect = useCallback((nodeId) => {
    clearSelection();
    setSelectedNodeId(nodeId);
  }, [clearSelection]);

  const handleLightSelect = useCallback((lightId) => {
    clearSelection();
    setSelectedLightId(lightId);
  }, [clearSelection]);

  const handleDwellSelect = useCallback((dwellId) => {
    clearSelection();
    setSelectedDwellId(dwellId);
  }, [clearSelection]);

  const handlePathSelect = useCallback((pathId) => {
    if (drawingPath) return;
    clearSelection();
    setSelectedPathId(pathId);
  }, [drawingPath, clearSelection]);

  const handleVertexDrag = useCallback(
    (roomId, vertexIndex, newPos) => {
      const updated = {
        ...floorPlan,
        rooms: floorPlan.rooms.map((room) => {
          if (room.id !== roomId) return room;
          const newPolygon = [...room.polygon];
          newPolygon[vertexIndex] = newPos;
          return { ...room, polygon: newPolygon };
        }),
      };
      saveFloorPlan(updated);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleEdgeDrag = useCallback(
    (roomId, newPolygon) => {
      const updated = {
        ...floorPlan,
        rooms: floorPlan.rooms.map((room) =>
          room.id === roomId ? { ...room, polygon: newPolygon } : room
        ),
      };
      saveFloorPlan(updated);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleRoomUpdate = useCallback(
    (roomId, updates) => {
      const updated = {
        ...floorPlan,
        rooms: floorPlan.rooms.map((room) =>
          room.id === roomId ? { ...room, ...updates } : room
        ),
      };
      saveFloorPlan(updated);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleRoomDelete = useCallback(
    (roomId) => {
      const updated = {
        ...floorPlan,
        rooms: floorPlan.rooms.filter((r) => r.id !== roomId),
      };
      saveFloorPlan(updated);
      setSelectedRoomId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleDoorDrag = useCallback(
    (doorId, newPos) => {
      saveFloorPlan({
        ...floorPlan,
        doors: (floorPlan.doors || []).map((d) =>
          d.id === doorId ? { ...d, ...newPos } : d
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleDoorUpdate = useCallback(
    (doorId, updates) => {
      saveFloorPlan({
        ...floorPlan,
        doors: (floorPlan.doors || []).map((d) =>
          d.id === doorId ? { ...d, ...updates } : d
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleDoorDelete = useCallback(
    (doorId) => {
      saveFloorPlan({
        ...floorPlan,
        doors: (floorPlan.doors || []).filter((d) => d.id !== doorId),
      });
      setSelectedDoorId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddDoor = useCallback(() => {
    const id = `door-${Date.now()}`;
    const newDoor = {
      id,
      type: "swing",
      x: Math.round((floorPlan.width / 2) * 10) / 10,
      y: Math.round((floorPlan.height / 2) * 10) / 10,
      width: 0.9,
      angle: 0,
    };
    saveFloorPlan({ ...floorPlan, doors: [...(floorPlan.doors || []), newDoor] });
    setSelectedRoomId(null);
    setSelectedDoorId(id);
  }, [floorPlan, saveFloorPlan]);

  const handleStairDrag = useCallback(
    (stairId, newPos) => {
      saveFloorPlan({
        ...floorPlan,
        stairs: (floorPlan.stairs || []).map((s) =>
          s.id === stairId ? { ...s, ...newPos } : s
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleStairUpdate = useCallback(
    (stairId, updates) => {
      saveFloorPlan({
        ...floorPlan,
        stairs: (floorPlan.stairs || []).map((s) =>
          s.id === stairId ? { ...s, ...updates } : s
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleStairDelete = useCallback(
    (stairId) => {
      saveFloorPlan({
        ...floorPlan,
        stairs: (floorPlan.stairs || []).filter((s) => s.id !== stairId),
      });
      setSelectedStairId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddStairs = useCallback(() => {
    const id = `stairs-${Date.now()}`;
    const newStairs = {
      id,
      x: Math.round((floorPlan.width / 2) * 10) / 10,
      y: Math.round((floorPlan.height / 2) * 10) / 10,
      width: 1.0,
      length: 3.0,
      angle: 0,
      direction: "up",
    };
    saveFloorPlan({ ...floorPlan, stairs: [...(floorPlan.stairs || []), newStairs] });
    setSelectedRoomId(null);
    setSelectedDoorId(null);
    setSelectedStairId(id);
  }, [floorPlan, saveFloorPlan]);

  // ---- Nodes ----
  const handleNodeDrag = useCallback(
    (nodeId, newPos) => {
      const room = roomAt(newPos.x, newPos.y, floorPlan.rooms);
      saveFloorPlan({
        ...floorPlan,
        nodes: (floorPlan.nodes || []).map((n) =>
          n.id === nodeId ? { ...n, ...newPos, room } : n
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleNodeUpdate = useCallback(
    (nodeId, updates) => {
      saveFloorPlan(updateMapNode(floorPlan, nodeId, updates));
    },
    [floorPlan, saveFloorPlan]
  );

  const handleNodeDelete = useCallback(
    (nodeId) => {
      saveFloorPlan({
        ...floorPlan,
        nodes: (floorPlan.nodes || []).filter((n) => n.id !== nodeId),
      });
      setSelectedNodeId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddNode = useCallback(
    (kind) => {
      setAddMenuOpen(false);
      setNodeSubOpen(false);
      const id = `node-${Date.now()}`;
      const x = Math.round((floorPlan.width / 2) * 10) / 10;
      const y = Math.round((floorPlan.height / 2) * 10) / 10;
      const flags = nodeKindFlags(kind);
      const newNode = {
        id,
        label: "Node",
        kind,
        ...flags,
        x,
        y,
        room: roomAt(x, y, floorPlan.rooms),
        sensors: [
          { id: `s-${Date.now().toString(36)}`, rotation: 90, fovAngle: 80, rangeM: 4, reliableM: 2 },
        ],
        camera: flags.hasCamera ? { rotation: 90, fovAngle: 67, rangeM: 5 } : null,
      };
      saveFloorPlan({ ...floorPlan, nodes: [...(floorPlan.nodes || []), newNode] });
      clearSelection();
      setSelectedNodeId(id);
    },
    [floorPlan, saveFloorPlan, clearSelection]
  );

  // Place a node marker pre-linked to a detected hardware MAC.
  const handlePlaceDetectedNode = useCallback(
    (reg) => {
      const caps = reg.caps || {};
      const hasCamera = !!caps.camera;
      const hasLux = caps.lux !== false;
      const kind = hasCamera ? (hasLux ? "full" : "full_nolux") : hasLux ? "simple" : "simple_nolux";
      const id = `node-${Date.now()}`;
      const x = Math.round((floorPlan.width / 2) * 10) / 10;
      const y = Math.round((floorPlan.height / 2) * 10) / 10;
      const flags = nodeKindFlags(kind);
      const newNode = {
        id,
        label: reg.alias || "Node",
        kind,
        ...flags,
        mac: reg.mac,
        x,
        y,
        room: roomAt(x, y, floorPlan.rooms),
        sensors: [
          { id: `s-${Date.now().toString(36)}`, rotation: 90, fovAngle: 80, rangeM: 4, reliableM: 2 },
        ],
        camera: flags.hasCamera ? { rotation: 90, fovAngle: 67, rangeM: 5 } : null,
      };
      saveFloorPlan({ ...floorPlan, nodes: [...(floorPlan.nodes || []), newNode] });
      clearSelection();
      setSelectedNodeId(id);
    },
    [floorPlan, saveFloorPlan, clearSelection]
  );

  // ---- Lights ----
  const handleLightDrag = useCallback(
    (lightId, newPos) => {
      const room = roomAt(newPos.x, newPos.y, floorPlan.rooms);
      saveFloorPlan({
        ...floorPlan,
        lights: (floorPlan.lights || []).map((l) =>
          l.id === lightId ? { ...l, ...newPos, room } : l
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleLightUpdate = useCallback(
    (lightId, updates) => {
      saveFloorPlan({
        ...floorPlan,
        lights: (floorPlan.lights || []).map((l) =>
          l.id === lightId ? { ...l, ...updates } : l
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleLightDelete = useCallback(
    (lightId) => {
      saveFloorPlan({
        ...floorPlan,
        lights: (floorPlan.lights || []).filter((l) => l.id !== lightId),
      });
      setSelectedLightId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddLight = useCallback(() => {
    const id = `light-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    // Stagger new lights diagonally so they don't stack exactly on top of one
    // another (overlapping fixtures are impossible to select/edit separately).
    const count = (floorPlan.lights || []).length;
    const offset = (count % 6) * 0.4;
    const x = Math.round((floorPlan.width / 2 + offset) * 10) / 10;
    const y = Math.round((floorPlan.height / 2 + offset) * 10) / 10;
    const newLight = {
      id,
      label: `Light ${count + 1}`,
      x,
      y,
      room: roomAt(x, y, floorPlan.rooms),
      bulbs: [],
    };
    saveFloorPlan({ ...floorPlan, lights: [...(floorPlan.lights || []), newLight] });
    clearSelection();
    setSelectedLightId(id);
  }, [floorPlan, saveFloorPlan, clearSelection]);

  // ---- Dwell zones (resting places / furniture) ----
  const handleDwellDrag = useCallback(
    (dwellId, newPos) => {
      saveFloorPlan({
        ...floorPlan,
        dwellZones: (floorPlan.dwellZones || []).map((z) => {
          if (z.id !== dwellId) return z;
          const moved = { ...z, ...newPos };
          return {
            ...moved,
            room: roomAt(moved.x + (moved.w || 0) / 2, moved.y + (moved.h || 0) / 2, floorPlan.rooms),
          };
        }),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleDwellUpdate = useCallback(
    (dwellId, updates) => {
      saveFloorPlan({
        ...floorPlan,
        dwellZones: (floorPlan.dwellZones || []).map((z) =>
          z.id === dwellId ? { ...z, ...updates } : z
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleDwellDelete = useCallback(
    (dwellId) => {
      saveFloorPlan({
        ...floorPlan,
        dwellZones: (floorPlan.dwellZones || []).filter((z) => z.id !== dwellId),
      });
      setSelectedDwellId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddDwell = useCallback(() => {
    const id = `dwell-${Date.now()}`;
    const w = 1.4;
    const h = 0.8;
    const x = Math.round((floorPlan.width / 2 - w / 2) * 100) / 100;
    const y = Math.round((floorPlan.height / 2 - h / 2) * 100) / 100;
    const newZone = {
      id,
      label: "Resting place",
      x,
      y,
      w,
      h,
      room: roomAt(x + w / 2, y + h / 2, floorPlan.rooms),
    };
    saveFloorPlan({ ...floorPlan, dwellZones: [...(floorPlan.dwellZones || []), newZone] });
    clearSelection();
    setSelectedDwellId(id);
  }, [floorPlan, saveFloorPlan, clearSelection]);

  // ---- Walk-paths ----
  const handleStartDrawPath = useCallback(() => {
    clearSelection();
    setDraftPoints([]);
    setDrawingPath(true);
  }, [clearSelection]);

  const handleAddPathPoint = useCallback((pt) => {
    setDraftPoints((prev) => [...prev, pt]);
  }, []);

  const handleFinishPath = useCallback(() => {
    if (draftPoints.length >= 2) {
      const id = `path-${Date.now()}`;
      const room = roomAt(draftPoints[0].x, draftPoints[0].y, floorPlan.rooms);
      saveFloorPlan({
        ...floorPlan,
        paths: [...(floorPlan.paths || []), { id, room, points: draftPoints }],
      });
    }
    setDrawingPath(false);
    setDraftPoints([]);
  }, [draftPoints, floorPlan, saveFloorPlan]);

  const handleCancelPath = useCallback(() => {
    setDrawingPath(false);
    setDraftPoints([]);
  }, []);

  const handlePathDelete = useCallback(
    (pathId) => {
      saveFloorPlan({
        ...floorPlan,
        paths: (floorPlan.paths || []).filter((p) => p.id !== pathId),
      });
      setSelectedPathId(null);
    },
    [floorPlan, saveFloorPlan]
  );

  const handlePathUpdate = useCallback(
    (pathId, updates) => {
      saveFloorPlan({
        ...floorPlan,
        paths: (floorPlan.paths || []).map((p) =>
          p.id === pathId ? { ...p, ...updates } : p
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handlePathReverse = useCallback(
    (pathId) => {
      saveFloorPlan({
        ...floorPlan,
        paths: (floorPlan.paths || []).map((p) =>
          p.id === pathId ? { ...p, points: [...(p.points || [])].reverse() } : p
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handlePathPointDrag = useCallback(
    (pathId, index, newPos) => {
      saveFloorPlan({
        ...floorPlan,
        paths: (floorPlan.paths || []).map((p) => {
          if (p.id !== pathId) return p;
          const points = (p.points || []).map((pt, i) => (i === index ? newPos : pt));
          return { ...p, room: roomAt(points[0].x, points[0].y, floorPlan.rooms), points };
        }),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleSuggestPlacement = useCallback(
    (nodeId) => {
      const node = (floorPlan.nodes || []).find((n) => n.id === nodeId);
      if (!node) return;
      const paths = floorPlan.paths || [];
      if (paths.length === 0) {
        window.alert("Draw a walk-path first, then suggest a placement.");
        return;
      }
      // Prefer a path in the same room as the node, else the first path.
      const path =
        paths.find((p) => node.room && p.room === node.room) || paths[0];
      const room = floorPlan.rooms.find((r) => r.id === path.room);
      if (!room) {
        window.alert("The selected path is not inside a room.");
        return;
      }
      const sensor = node.sensors?.[0];
      const best = suggestPlacement(
        path.points,
        room,
        sensor?.fovAngle ?? 80,
        sensor?.rangeM ?? 4
      );
      if (!best) return;
      const sensors = (node.sensors || []).map((s, i) =>
        i === 0 ? { ...s, rotation: best.rotation } : s
      );
      saveFloorPlan({
        ...floorPlan,
        nodes: (floorPlan.nodes || []).map((n) =>
          n.id === nodeId
            ? { ...n, x: best.x, y: best.y, room: roomAt(best.x, best.y, floorPlan.rooms), sensors }
            : n
        ),
      });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleAddRoom = useCallback(() => {
    const id = `room-${Date.now()}`;
    const cx = floorPlan.width / 2;
    const cy = floorPlan.height / 2;
    const newRoom = {
      id,
      name: "New Room",
      color: "#888888",
      polygon: [
        { x: cx - 1.5, y: cy - 1.5 },
        { x: cx + 1.5, y: cy - 1.5 },
        { x: cx + 1.5, y: cy + 1.5 },
        { x: cx - 1.5, y: cy + 1.5 },
      ],
    };
    const updated = { ...floorPlan, rooms: [...floorPlan.rooms, newRoom] };
    saveFloorPlan(updated);
    setSelectedRoomId(id);
  }, [floorPlan, saveFloorPlan]);

  const handleReset = useCallback(() => {
    saveFloorPlan(INITIAL_FLOOR_PLAN);
    setSelectedRoomId(null);
  }, [saveFloorPlan]);

  // Shrink the apartment container to tightly wrap all rooms, and shift the
  // whole layout so the bounding box starts at the origin (0,0).
  const handleAutoFit = useCallback(() => {
    if (!floorPlan.rooms.length) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const room of floorPlan.rooms) {
      for (const p of room.polygon) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
    }
    const round = (n) => Math.round(n * 10) / 10;
    const rooms = floorPlan.rooms.map((room) => ({
      ...room,
      polygon: room.polygon.map((p) => ({
        x: round(p.x - minX),
        y: round(p.y - minY),
      })),
    }));
    saveFloorPlan({
      ...floorPlan,
      width: round(maxX - minX),
      height: round(maxY - minY),
      rooms,
    });
  }, [floorPlan, saveFloorPlan]);

  const handleContainerUpdate = useCallback(
    (updates) => {
      saveFloorPlan({ ...floorPlan, ...updates });
    },
    [floorPlan, saveFloorPlan]
  );

  const handleEditingChange = useCallback((editing) => {
    setIsEditing(editing);
    if (!editing) {
      setAddMenuOpen(false);
      setNodeSubOpen(false);
      setDrawingPath(false);
      setDraftPoints([]);
    }
  }, []);

  return (
    <div className="house-map-page">
      <div className="house-map-header">
        <h2>House Map</h2>
        <div className="house-map-toolbar">
          <button
            className={`map-edit-toggle ${isEditing ? "editing" : "locked"}`}
            onClick={() => handleEditingChange(!isEditing)}
          >
            {isEditing ? "Done" : "Edit map"}
          </button>
          <span className={`sync-status sync-${syncStatus}`}>
            {syncStatus === "loading" && "Loading…"}
            {syncStatus === "saving" && "Saving…"}
            {syncStatus === "saved" && "Saved"}
            {syncStatus === "error" && "Offline (local only)"}
          </span>
          <div className="toolbar-toggles">
            {showCoverage && overallCoverage !== null && (
              <span
                className="coverage-score"
                style={{
                  color:
                    overallCoverage >= 0.9 ? "#2ecc71" : overallCoverage >= 0.6 ? "#d9a14a" : "#e74c3c",
                }}
              >
                {Math.round(overallCoverage * 100)}% covered
              </span>
            )}
            {showCoverage && dwellCov !== null && (
              <span
                className="coverage-score"
                title="Resting places whose seat sits inside a 2 m reliable ring"
                style={{
                  color: dwellCov >= 0.9 ? "#2ecc71" : dwellCov >= 0.6 ? "#d9a14a" : "#e74c3c",
                }}
              >
                {Math.round(dwellCov * 100)}% seats
              </span>
            )}
            <label className="toolbar-toggle">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
              />
              Grid
            </label>
            <label className="toolbar-toggle">
              <input
                type="checkbox"
                checked={showHeatmap}
                onChange={(e) => setShowHeatmap(e.target.checked)}
              />
              Heatmap
            </label>
            <label className="toolbar-toggle">
              <input
                type="checkbox"
                checked={showCoverage}
                onChange={(e) => setShowCoverage(e.target.checked)}
              />
              Coverage
            </label>
            <span className="toolbar-sep" />
            {[
              { key: "structure", label: "Rooms" },
              { key: "nodes", label: "Nodes" },
              { key: "lights", label: "Lights" },
              { key: "dwell", label: "Resting" },
              { key: "paths", label: "Paths" },
            ].map((l) => (
              <button
                key={l.key}
                className={`layer-toggle ${layers[l.key] ? "on" : "off"}`}
                onClick={() => setLayers((prev) => ({ ...prev, [l.key]: !prev[l.key] }))}
                title={`${layers[l.key] ? "Hide" : "Show"} ${l.label}`}
              >
                {layers[l.key] ? "👁" : "🚫"} {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="house-map-content">
        <div className="house-map-canvas-container">
          {isEditing && <div className="map-toolbar">
            {drawingPath ? (
              <div className="map-toolbar-drawing">
                <span className="map-drawing-hint">Click to add points</span>
                <button className="btn-secondary" onClick={handleFinishPath}>
                  Finish
                </button>
                <button className="btn-secondary btn-danger" onClick={handleCancelPath}>
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  className="map-add-btn"
                  onClick={() => {
                    setAddMenuOpen((o) => !o);
                    setNodeSubOpen(false);
                  }}
                  title="Add elements to the map"
                >
                  ＋ Add ▾
                </button>
                {addMenuOpen && (
                  <div className="map-add-menu">
                    <button onClick={() => { handleAddRoom(); setAddMenuOpen(false); }}>Room</button>
                    <button onClick={() => { handleAddDoor(); setAddMenuOpen(false); }}>Door</button>
                    <button onClick={() => { handleAddStairs(); setAddMenuOpen(false); }}>Stairs</button>
                    <button onClick={() => { handleAddLight(); setAddMenuOpen(false); }}>Light</button>
                    <button onClick={() => { handleAddDwell(); setAddMenuOpen(false); }}>Resting place</button>
                    <button className="map-add-sub-toggle" onClick={() => setNodeSubOpen((o) => !o)}>
                      Nodes {nodeSubOpen ? "▾" : "▸"}
                    </button>
                    {nodeSubOpen &&
                      Object.entries(NODE_KIND_LABELS).map(([kind, label]) => (
                        <button
                          key={kind}
                          className="map-add-sub-item"
                          onClick={() => { handleAddNode(kind); setAddMenuOpen(false); setNodeSubOpen(false); }}
                        >
                          {label}
                        </button>
                      ))}
                    <div className="map-add-sep" />
                    <button onClick={() => { handleStartDrawPath(); setAddMenuOpen(false); }}>Draw Path</button>
                    <button onClick={() => { handleAutoFit(); setAddMenuOpen(false); }}>Auto-fit</button>
                    <div className="map-add-sep" />
                    <button className="map-add-danger" onClick={() => { handleReset(); setAddMenuOpen(false); }}>
                      Reset
                    </button>
                  </div>
                )}
              </>
            )}
          </div>}
          <FloorPlanCanvas
            floorPlan={floorPlan}
            editable={isEditing}
            selectedRoomId={selectedRoomId}
            selectedDoorId={selectedDoorId}
            selectedStairId={selectedStairId}
            selectedNodeId={selectedNodeId}
            selectedLightId={selectedLightId}
            selectedDwellId={selectedDwellId}
            showGrid={showGrid}
            showHeatmap={showHeatmap}
            layers={layers}
            drawingPath={drawingPath}
            draftPoints={draftPoints}
            selectedPathId={selectedPathId}
            onRoomSelect={handleRoomSelect}
            onVertexDrag={handleVertexDrag}
            onEdgeDrag={handleEdgeDrag}
            onDoorSelect={handleDoorSelect}
            onDoorDrag={handleDoorDrag}
            onStairSelect={handleStairSelect}
            onStairDrag={handleStairDrag}
            onNodeSelect={handleNodeSelect}
            onNodeDrag={handleNodeDrag}
            onLightSelect={handleLightSelect}
            onLightDrag={handleLightDrag}
            onDwellSelect={handleDwellSelect}
            onDwellDrag={handleDwellDrag}
            onDwellResize={(id, size) => handleDwellUpdate(id, size)}
            onPathSelect={handlePathSelect}
            onAddPathPoint={handleAddPathPoint}
            onPathPointDrag={handlePathPointDrag}
          />
        </div>

        {isEditing && <div className="house-map-sidebar">
          {selectedNode ? (
            <NodeEditor
              node={selectedNode}
              roomName={roomNameById(selectedNode.room)}
              registryNodes={nodeRegistry}
              usedMacs={usedMacs}
              onRefreshNodes={refreshNodes}
              onUpdate={(updates) => handleNodeUpdate(selectedNodeId, updates)}
              onDelete={() => handleNodeDelete(selectedNodeId)}
              onDeselect={() => setSelectedNodeId(null)}
              onSuggest={() => handleSuggestPlacement(selectedNodeId)}
            />
          ) : selectedLight ? (
            <LightEditor
              light={selectedLight}
              roomName={roomNameById(selectedLight.room)}
              onUpdate={(updates) => handleLightUpdate(selectedLightId, updates)}
              onDelete={() => handleLightDelete(selectedLightId)}
              onDeselect={() => setSelectedLightId(null)}
            />
          ) : selectedDwell ? (
            <DwellEditor
              zone={selectedDwell}
              roomName={roomNameById(selectedDwell.room)}
              covered={coveredByReliable(dwellCenter(selectedDwell), floorPlan.nodes)}
              onUpdate={(updates) => handleDwellUpdate(selectedDwellId, updates)}
              onDelete={() => handleDwellDelete(selectedDwellId)}
              onDeselect={() => setSelectedDwellId(null)}
            />
          ) : selectedPath ? (
            <PathEditor
              path={selectedPath}
              roomName={roomNameById(selectedPath.room)}
              nodes={floorPlan.nodes || []}
              onUpdate={(updates) => handlePathUpdate(selectedPathId, updates)}
              onReverse={() => handlePathReverse(selectedPathId)}
              onDelete={() => handlePathDelete(selectedPathId)}
              onDeselect={() => setSelectedPathId(null)}
            />
          ) : selectedStair ? (
            <StairEditor
              stair={selectedStair}
              onUpdate={(updates) => handleStairUpdate(selectedStairId, updates)}
              onDelete={() => handleStairDelete(selectedStairId)}
              onDeselect={() => setSelectedStairId(null)}
            />
          ) : selectedDoor ? (
            <DoorEditor
              door={selectedDoor}
              onUpdate={(updates) => handleDoorUpdate(selectedDoorId, updates)}
              onDelete={() => handleDoorDelete(selectedDoorId)}
              onDeselect={() => setSelectedDoorId(null)}
            />
          ) : selectedRoom ? (
            <RoomEditor
              room={selectedRoom}
              onUpdate={(updates) => handleRoomUpdate(selectedRoomId, updates)}
              onDelete={() => handleRoomDelete(selectedRoomId)}
              onDeselect={() => setSelectedRoomId(null)}
            />
          ) : (
            <div className="sidebar-placeholder">
              <ContainerEditor
                floorPlan={floorPlan}
                onUpdate={handleContainerUpdate}
              />
              {unlinkedNodes.length > 0 && (
                <div className="unlinked-nodes">
                  <div className="unlinked-nodes-head">
                    <h4>Detected hardware ({unlinkedNodes.length})</h4>
                    <button className="btn-mini" onClick={refreshNodes} title="Refresh">
                      ↻
                    </button>
                  </div>
                  <p className="editor-hint">
                    Nodes seen by the presence engine but not yet placed on the map.
                  </p>
                  {unlinkedNodes.map((n) => (
                    <button
                      key={n.mac}
                      className="unlinked-node-item"
                      onClick={() => handlePlaceDetectedNode(n)}
                      title="Place on map and link"
                    >
                      <span className={`hw-dot ${n.online ? "on" : "off"}`} />
                      <span className="unlinked-node-label">{n.alias || n.mac}</span>
                      <code className="unlinked-node-mac">{n.mac}</code>
                      <span className="unlinked-node-add">+ Place</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="room-list">
                <h4>Rooms</h4>
                {floorPlan.rooms.map((room) => (
                  <button
                    key={room.id}
                    className="room-list-item"
                    onClick={() => setSelectedRoomId(room.id)}
                  >
                    <span
                      className="room-color-dot"
                      style={{ backgroundColor: room.color }}
                    />
                    {room.name}
                  </button>
                ))}
              </div>
              {(floorPlan.doors || []).length > 0 && (
                <div className="room-list">
                  <h4>Doors</h4>
                  {(floorPlan.doors || []).map((door, i) => (
                    <button
                      key={door.id}
                      className="room-list-item"
                      onClick={() => handleDoorSelect(door.id)}
                    >
                      <span
                        className="room-color-dot"
                        style={{
                          backgroundColor:
                            door.type === "sliding"
                              ? "#4ad2ff"
                              : door.type === "open"
                              ? "#7CFC9E"
                              : "#ffd24a",
                        }}
                      />
                      {door.type === "open"
                        ? "Open passage"
                        : door.type === "sliding"
                        ? "Sliding door"
                        : "Swing door"}{" "}
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
              {(floorPlan.stairs || []).length > 0 && (
                <div className="room-list">
                  <h4>Stairs</h4>
                  {(floorPlan.stairs || []).map((s, i) => (
                    <button
                      key={s.id}
                      className="room-list-item"
                      onClick={() => handleStairSelect(s.id)}
                    >
                      <span
                        className="room-color-dot"
                        style={{ backgroundColor: "#c08cff" }}
                      />
                      Stairs {i + 1} ({s.direction === "down" ? "down" : "up"})
                    </button>
                  ))}
                </div>
              )}
              {(floorPlan.nodes || []).length > 0 && (
                <div className="room-list">
                  <h4>Nodes</h4>
                  {(floorPlan.nodes || []).map((n) => (
                    <button
                      key={n.id}
                      className="room-list-item"
                      onClick={() => handleNodeSelect(n.id)}
                    >
                      <span className="room-color-dot" style={{ backgroundColor: "#2ec7b8" }} />
                      {n.label || "Node"}
                      <span className="list-sub">
                        {NODE_KIND_LABELS[n.kind] || n.kind}
                        {n.room ? ` · ${roomNameById(n.room)}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {(floorPlan.lights || []).length > 0 && (
                <div className="room-list">
                  <h4>Lights</h4>
                  {(floorPlan.lights || []).map((l) => (
                    <button
                      key={l.id}
                      className="room-list-item"
                      onClick={() => handleLightSelect(l.id)}
                    >
                      <span className="room-color-dot" style={{ backgroundColor: "#f5d000" }} />
                      {l.label || "Light"}
                      <span className="list-sub">
                        {(l.bulbs || []).length} bulb{(l.bulbs || []).length === 1 ? "" : "s"}
                        {l.room ? ` · ${roomNameById(l.room)}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {(floorPlan.dwellZones || []).length > 0 && (
                <div className="room-list">
                  <h4>Resting Places</h4>
                  {(floorPlan.dwellZones || []).map((z) => {
                    const covered = coveredByReliable(dwellCenter(z), floorPlan.nodes);
                    return (
                      <button
                        key={z.id}
                        className="room-list-item"
                        onClick={() => handleDwellSelect(z.id)}
                      >
                        <span
                          className="room-color-dot"
                          style={{ backgroundColor: covered ? "#2ecc71" : "#e0884a" }}
                        />
                        {z.label || "Resting place"}
                        <span className="list-sub">
                          {covered ? "reliable" : "blind"}
                          {z.room ? ` · ${roomNameById(z.room)}` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {(floorPlan.paths || []).length > 0 && (
                <div className="room-list">
                  <h4>Walk-paths</h4>
                  {(floorPlan.paths || []).map((p, i) => (
                    <button
                      key={p.id}
                      className="room-list-item"
                      onClick={() => handlePathSelect(p.id)}
                    >
                      <span className="room-color-dot" style={{ backgroundColor: "#ffd24a" }} />
                      Path {i + 1}
                      <span className="list-sub">
                        {(p.points || []).length} pts
                        {p.room ? ` · ${roomNameById(p.room)}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

export default HouseMap;
